
import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import PinnedChartReport from '~/components/Reports/PinnedChartReport.vue'
import PinnedCodeCoverageReport from '~/components/Reports/PinnedCodeCoverageReport.vue'

import PinnedReportsMixin from '~/mixins/pinnedReportsMixin'
import RepoDetailMixin from '~/mixins/repoDetailMixin'

import { RepoPerms } from '~/types/permTypes'
import { LoadingConditions, ReportPageT } from '~/types/reportTypes'
import {
  AddSubRepoInput,
  PinnedReportInput,
  ReportLevel,
  Repository,
  RepositoryConnection,
  RepositoryKindChoices
} from '~/types/types'

import ReportBaseGQLQuery from '~/apollo/queries/reports/reportBase.gql'
import subRepositoryListGQLQuery from '~/apollo/queries/repository/subRepositoryList.gql'

import { OwnerDetailActions, OwnerDetailGetters } from '~/store/owner/detail'

import AddSubRepositoryGQLMutation from '~/apollo/mutations/repository/addSubRepo.gql'

import { GraphqlQueryResponse } from '~/types/apollo-graphql-types'
import { IssueOccurrenceDistributionType } from '~/types/issues'

import { resolveNodes } from '~/utils/array'
import { formatRepoName, shortenLargeNumber } from '~/utils/string'
import { canAccessFeature } from '~/utils/billing'
import { FeatureType } from '~/types/features'
import { KnowledgeSyncFetchArgs, KnowledgeSyncModuleState } from '~/store/knowledge/sync'
import { knowledgeSyncLabel } from '~/utils/knowledge/sync'

import PaginationMixin from '~/mixins/paginationMixin'

export interface Widget {
  title: string
  value_display: number
  link: string
  description: string
  hint?: string
  has_trend_value: boolean
  trend_direction: string | null
  trend_value: string
  trend_display: string
  trend_positive: boolean
}

type FetchSubRepositoriesArgsT = {
  searchQueryFromEmit?: string
  currentPageFromEmit?: number
  refetch?: boolean
}

const KNOWLEDGE_SYNC_NS = 'knowledge/sync'

type MetadataViewPropsT = Partial<
  Pick<
    Repository,
    | 'availableAnalyzers'
    | 'defaultBranchName'
    | 'latestAnalysisRun'
    | 'runs'
    | 'isActivated'
    | 'vcsProvider'
    | 'issueOccurrenceDistributionByIssueType'
  >
> & {
  ready?: boolean
  issuesPrevented?: string | number
  showKnowledgeSync?: boolean
  lastKnowledgeSync?: string
}

/**
 * Repo home page
 */
export default defineComponent({
  name: 'Overview',
  layout: 'repository',
  components: {
    PinnedChartReport,
    PinnedCodeCoverageReport
  },
  mixins: [PaginationMixin, PinnedReportsMixin, RepoDetailMixin],
  /**
   * Head hook to add meta details to the page
   * @return {Record<string, string>}
   */
  head(): Record<string, string> {
    const { owner, repo } = this.$route.params
    return {
      title: `${owner}/${formatRepoName(repo)} • DeepSource`
    }
  },
  data() {
    return {
      LoadingConditions,
      ReportLevel,
      issuesPrevented: 0 as string | number,
      metadataReady: false,
      // Snapshotted with metadataReady so the card's row count is fixed at reveal (no mid-load shift).
      syncRowVisible: false,
      perPageCount: 10,
      currentPage: 1,
      totalCount: 1,
      addSubRepositoryLoading: false,
      // This is intentionally made `true` initially since the monorepo status is pre-fetched at the layout level
      // and toggling it to `true` afterward will result in the empty state shown at first before the skeleton loaders for the sub repo list
      fetchingSubRepositoryList: true,
      loading: false,
      showMetadataViewDialog: false,
      showAddSubRepositoryModal: false,
      showErrorInAddSubRepositoryModal: false,
      addSubRepositoryErrorMsg: '',
      searchQuery: '',
      timerId: null as ReturnType<typeof setTimeout> | null,
      subRepositoryList: [] as Repository[]
    }
  },
  /**
   * Fetch hook to fetch all the basic details for a repository
   * @return {Promise<void>}
   */
  async fetch(): Promise<void> {
    try {
      const baseArgs = {
        ...this.baseRouteParams,
        provider: this.$providerMetaMap[this.provider].value
      }

      if (this.isMonorepo) {
        let refetch = false

        if (process.client) {
          const { provider, owner, name: repo } = this.baseRouteParams

          const store = `${provider}-${owner}-${repo}`
          const storeKey = 're-fetch-sub-repositories'

          refetch = Boolean(this.$localStore.get(store, storeKey))
          this.$localStore.set(store, storeKey, false)
        }

        await this.fetchSubRepositories({ refetch })
        return
      }

      await Promise.all([this.fetchRepoPerms(baseArgs), this.fetchRepoDetails(baseArgs)])

      // Resolve AI review up front (perms are now known) so the sync row's presence is decided
      // before the skeleton paints — the card renders its final shape once, with no layout shift.
      // Cache-first: RepoHeader in the layout already fetches owner details, so this is a cache hit.
      if (this.hasKnowledgeAccess) {
        await this.fetchOwnerDetails({ login: this.owner, provider: this.provider })
      }

      // Fetch pinned reports list only if the user has the required perms
      if (this.canViewPinnedReports) {
        // Fetch the list of report keys pinned at the repository level
        await this.fetchPinnedReports({ level: ReportLevel.Repository })
      }

      const parallelFetches: Array<Promise<unknown>> = [
        this.fetchIssueOccurrenceDistributionCounts({
          ...baseArgs,
          distributionType: IssueOccurrenceDistributionType.ISSUE_TYPE
        }),
        this.fetchRepoRunCount({ ...baseArgs, status: 'pend' }),
        this.$fetchGraphqlData(ReportBaseGQLQuery, {
          level: ReportLevel.Repository,
          objectId: this.repository.id,
          key: ReportPageT.ISSUES_PREVENTED
        }).then((response: GraphqlQueryResponse) => {
          this.issuesPrevented = (response.data.report?.currentValue as number) ?? 0
        })
      ]

      if (this.showKnowledgeSync) {
        parallelFetches.push(
          this.fetchSyncStatus({
            provider: baseArgs.provider,
            owner: this.owner,
            name: this.repositoryName
          }).catch((e: Error) =>
            this.$logErrorAndToast(e, 'Failed to load the knowledge sync status.')
          )
        )
      }

      await Promise.all(parallelFetches)
    } catch (e) {
      this.$logErrorAndToast(e as Error, 'There was a problem loading this repository.')
    } finally {
      this.loading = false
      this.syncRowVisible = this.showKnowledgeSync
      this.metadataReady = true
    }
  },

  created() {
    // This is to prevent showing skeleton loaders if hitting the cache
    // `loading` data property is set to `true` only if `$fetchState.pending` is `true` after `300ms`
    if (process.client) {
      this.timerId = setTimeout(() => {
        if (this.$fetchState.pending) {
          this.loading = true
        }
      }, 300)
    }
  },

  /**
   * The `mounted` hook
   * Bind event bus listeners
   *
   * @returns {void}
   */
  mounted(): void {
    this.$root.$on('toggle-metadata-view-dialog', this.toggleMetadataViewDialog)
    this.$root.$on('update-pinned-reports', this.updatePinnedReportsHandler)
  },

  /**
   * The `beforeDestroy` hook
   * Unbind event bus listeners
   *
   * @returns {void}
   */
  beforeDestroy(): void {
    this.$root.$on('toggle-metadata-view-dialog', this.toggleMetadataViewDialog)
    this.$root.$off('update-pinned-reports', this.updatePinnedReportsHandler)
    if (this.timerId) {
      clearTimeout(this.timerId)
    }
  },

  computed: {
    ...(mapState<KnowledgeSyncModuleState>(KNOWLEDGE_SYNC_NS, {
      knowledgeLastSyncedAt: (state: KnowledgeSyncModuleState) => state.lastSyncedAt
    }) as {
      knowledgeLastSyncedAt: () => KnowledgeSyncModuleState['lastSyncedAt']
    }),

    ...mapGetters('owner/detail', {
      aiReviewEnabled: OwnerDetailGetters.IS_AI_REVIEW_ENABLED
    }),

    /** Viewer can see Knowledge (perm) and the repo's plan includes it. Gates the ai-review fetch. */
    hasKnowledgeAccess(): boolean {
      const role = this.repoPerms?.permission
      if (!role || !this.$gateKeeper.repo(RepoPerms.VIEW_KNOWLEDGE, role)) return false
      return (
        this.$config.onPrem ||
        canAccessFeature(FeatureType.KNOWLEDGE, this.activeDashboardContext?.subscribed_plan_info)
      )
    },

    /** The sync row shows only when the viewer has access AND AI review is enabled for the owner. */
    showKnowledgeSync(): boolean {
      return this.hasKnowledgeAccess && this.aiReviewEnabled
    },

    lastKnowledgeSync(): string {
      return knowledgeSyncLabel(this.knowledgeLastSyncedAt)
    },

    allowPinningReports(): boolean {
      if (this.loading) {
        return false
      }
      return this.$gateKeeper.repo(RepoPerms.PIN_REPORTS, this.repoPerms.permission)
    },

    canAddSubRepository(): boolean {
      return this.$gateKeeper.repo(RepoPerms.ADD_SUB_REPOSITORY, this.repoPerms.permission)
    },

    canViewPinnedReports(): boolean {
      return this.$gateKeeper.repo(RepoPerms.VIEW_REPORTS, this.repoPerms.permission)
    },

    isMonorepo() {
      return this.repository.kind === RepositoryKindChoices.Monorepo
    },

    metadataViewProps(): MetadataViewPropsT {
      const {
        availableAnalyzers,
        defaultBranchName,
        issueOccurrenceDistributionByIssueType,
        latestAnalysisRun,
        runs
      } = this.repository

      return {
        availableAnalyzers,
        defaultBranchName,
        issueOccurrenceDistributionByIssueType,
        issuesPrevented: shortenLargeNumber(this.issuesPrevented as number),
        latestAnalysisRun,
        ready: this.metadataReady,
        runs,
        showKnowledgeSync: this.syncRowVisible,
        lastKnowledgeSync: this.lastKnowledgeSync
      }
    },

    metadataViewMobileProps(): MetadataViewPropsT {
      const {
        availableAnalyzers,
        defaultBranchName,
        isActivated,
        issueOccurrenceDistributionByIssueType,
        latestAnalysisRun,
        runs,
        vcsProvider
      } = this.repository

      return {
        availableAnalyzers,
        defaultBranchName,
        isActivated,
        issueOccurrenceDistributionByIssueType,
        issuesPrevented: shortenLargeNumber(this.issuesPrevented as number),
        latestAnalysisRun,
        runs,
        vcsProvider,
        showKnowledgeSync: this.showKnowledgeSync,
        lastKnowledgeSync: this.lastKnowledgeSync
      }
    },

    owner(): string {
      return this.$route.params.owner
    },

    provider(): string {
      return this.$route.params.provider
    },

    repositoryName(): string {
      return this.$route.params.repo
    },

    repositoryDisplayName(): string {
      return this.repository.displayName ?? this.$route.params.repo
    }
  },

  methods: {
    ...(mapActions(KNOWLEDGE_SYNC_NS, { fetchSyncStatus: 'fetchSyncStatus' }) as {
      fetchSyncStatus: (args: KnowledgeSyncFetchArgs) => Promise<void>
    }),
    ...mapActions('owner/detail', {
      fetchOwnerDetails: OwnerDetailActions.FETCH_OWNER_DETAILS
    }),

    async addSubRepository({
      branch,
      subRepositoryPath
    }: {
      branch: string
      subRepositoryPath: string
    }): Promise<void> {
      if (!branch || !subRepositoryPath) {
        this.showErrorInAddSubRepositoryModal = true
        this.addSubRepositoryErrorMsg = 'Both fields are required.'
        return
      }

      const args: AddSubRepoInput = {
        defaultBranchName: branch,
        monorepoId:
          this.repoIdMap[
            `${this.$providerMetaMap[this.provider].value}-${this.owner}-${this.$route.params.repo}`
          ],
        path: subRepositoryPath
      }

      this.addSubRepositoryLoading = true

      try {
        const {
          data: { addSubRepo }
        } = await this.$applyGraphqlMutation(AddSubRepositoryGQLMutation, { input: args })

        this.showErrorInAddSubRepositoryModal = false

        this.$router.push(
          this.$generateRoute(
            [(addSubRepo.repository as Repository).name, 'settings', 'code-review'],
            false
          )
        )

        // Re-fetch the list of sub-repositories
        this.fetchSubRepositories({ refetch: true })
      } catch (err) {
        const error = err as Error
        this.showErrorInAddSubRepositoryModal = true
        this.addSubRepositoryErrorMsg = `${error.message.replace('GraphQL error: ', '')}`

        this.$logErrorAndToast(error)
        this.addSubRepositoryLoading = false
      }
    },

    toggleAddSubRepositoryModalVisibility(visibilityStatus: boolean) {
      if (!visibilityStatus) {
        // Ensure the error message never stays if re-opening the modal
        this.showErrorInAddSubRepositoryModal = false
      }
      this.showAddSubRepositoryModal = visibilityStatus
    },

    async fetchSubRepositories({
      searchQueryFromEmit,
      currentPageFromEmit,
      refetch = false
    }: FetchSubRepositoriesArgsT = {}) {
      try {
        if (!this.fetchingSubRepositoryList) {
          this.fetchingSubRepositoryList = true
        }

        this.searchQuery = searchQueryFromEmit ?? this.searchQuery
        this.currentPage = currentPageFromEmit ?? this.currentPage

        const provider = this.$providerMetaMap[this.provider].value

        const response: GraphqlQueryResponse = await this.$fetchGraphqlData(
          subRepositoryListGQLQuery,
          {
            provider,
            owner: this.owner,
            name: this.repositoryName,
            nameFuzzy: this.searchQuery,
            first: this.perPageCount,
            after: this.$getGQLAfter(this.currentPage, this.perPageCount)
          },
          refetch
        )
        const { subRepos } = response.data.repository as { subRepos: RepositoryConnection }

        this.subRepositoryList = resolveNodes(subRepos)

        this.totalCount = subRepos.totalCount as number
      } catch (err) {
        this.$logErrorAndToast(err as Error)
      } finally {
        this.fetchingSubRepositoryList = false
      }
    },

    toggleMetadataViewDialog() {
      this.showMetadataViewDialog = !this.showMetadataViewDialog
    },

    /**
     * Handler method for the `update-pinned-reports` event
     * Invoke the `updatePinnedReports` method as part of the `pinnedReportsMixin`
     *
     * @param {Array<PinnedReportInput>} pinnedReports
     * @param {number} [reportSlotFromEmit]
     */
    async updatePinnedReportsHandler(
      pinnedReports: Array<PinnedReportInput>,
      reportSlotFromEmit: number
    ): Promise<void> {
      await this.updatePinnedReports(ReportLevel.Repository, pinnedReports, reportSlotFromEmit)
    }
  }
})
